import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../notifications/ToastProvider';
import { useSignalREventLog } from '../stores/useSignalREventLog';
import { createNotificationBuffer } from './notificationBuffer';
import {
  acquireNotificationHub,
  getNotificationHubConnectionState,
  releaseNotificationHub,
  whenNotificationHubStarted,
} from './notificationHubConnection';
import type { NotificationMessage } from './notificationMessage';
import { ENTITLEMENTS_QUERY_KEY } from '../entitlements';
import { queryKeysForNotifications } from './queryInvalidationMap';

const BUFFER_WINDOW_MS = 2000;

export type HubConnectionUiState = 'Disconnected' | 'Connecting' | 'Connected' | 'Reconnecting';

interface NotificationHubContextValue {
  connectionState: HubConnectionUiState;
}

const NotificationHubContext = createContext<NotificationHubContextValue>({
  connectionState: 'Disconnected',
});

export function useNotificationHubState(): HubConnectionUiState {
  return useContext(NotificationHubContext).connectionState;
}

interface NotificationHubProviderProps {
  children: ReactNode;
}

export function NotificationHubProvider({ children }: NotificationHubProviderProps) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { showInfo } = useToast();
  const [connectionState, setConnectionState] = useState<HubConnectionUiState>('Disconnected');
  const showInfoRef = useRef(showInfo);

  useEffect(() => {
    showInfoRef.current = showInfo;
  }, [showInfo]);

  useEffect(() => {
    if (!isAuthenticated) {
      setConnectionState('Disconnected');
      return;
    }

    const connection = acquireNotificationHub();
    setConnectionState(mapState(getNotificationHubConnectionState()));

    const buffer = createNotificationBuffer<NotificationMessage>(BUFFER_WINDOW_MS, (batch) => {
      if (batch.length === 0) return;

      const messages = batch
        .map((n) => n.message)
        .filter((m) => m != null && m.trim().length > 0);
      if (messages.length > 0) {
        showInfoRef.current(messages.join('<br/>'));
      }

      // Invalidation is never gated on silence — mute suppresses toast only.
      const roots = queryKeysForNotifications(batch);
      for (const queryKey of roots) {
        void queryClient.invalidateQueries({ queryKey });
      }
    });

    const onNotify = (message: NotificationMessage) => {
      // Append immediately (WPF SignalRClient.AllEvents) — do not wait for the toast buffer.
      useSignalREventLog.getState().append(message);
      buffer.push(message);
    };

    connection.on('NotifyDataChanged', onNotify);

    const syncState = () => setConnectionState(mapState(connection.state));
    const healEntitlements = () => {
      syncState();
      void queryClient.invalidateQueries({ queryKey: ENTITLEMENTS_QUERY_KEY });
    };
    connection.onreconnecting(syncState);
    connection.onreconnected(healEntitlements);
    connection.onclose(syncState);

    void whenNotificationHubStarted()
      .catch((error) => {
        console.error('SignalR notification hub failed to start', error);
      })
      .finally(syncState);

    return () => {
      connection.off('NotifyDataChanged', onNotify);
      buffer.dispose();
      releaseNotificationHub();
      setConnectionState('Disconnected');
    };
  }, [queryClient, isAuthenticated]);

  return (
    <NotificationHubContext.Provider value={{ connectionState }}>
      {children}
    </NotificationHubContext.Provider>
  );
}

function mapState(state: string | HubConnectionState): HubConnectionUiState {
  switch (state) {
    case HubConnectionState.Connected:
    case 'Connected':
      return 'Connected';
    case HubConnectionState.Connecting:
    case 'Connecting':
      return 'Connecting';
    case HubConnectionState.Reconnecting:
    case 'Reconnecting':
      return 'Reconnecting';
    default:
      return 'Disconnected';
  }
}
